import { TONE } from './constants'

export const SHAREPOINT_EXTERNAL_USER_EXPIRE_RECOMMENDED_DAYS = 90
export const SHAREPOINT_ANONYMOUS_LINKS_EXPIRE_RECOMMENDED_DAYS = 30
const SHAREPOINT_NO_EXPIRY_DAYS_VALUE = -1
const SHAREPOINT_MARK_NEW_FILES_SENSITIVE_BLOCK_EXTERNAL_SHARING = 'BlockExternalSharing'
const SHAREPOINT_MARK_NEW_FILES_SENSITIVE_ALLOW_EXTERNAL_SHARING = 'AllowExternalSharing'

export const SHAREPOINT_DEFAULT_SHARING_LINK_TYPE_MESSAGE_ID_BY_VALUE = {
  0: 'AI_RISK_REPORT.SHAREPOINT.DEFAULT_SHARING_LINK_TYPE.NONE',
  1: 'AI_RISK_REPORT.SHAREPOINT.DEFAULT_SHARING_LINK_TYPE.SPECIFIC_PEOPLE',
  2: 'AI_RISK_REPORT.SHAREPOINT.DEFAULT_SHARING_LINK_TYPE.ORGANIZATION_ONLY',
  3: 'AI_RISK_REPORT.SHAREPOINT.DEFAULT_SHARING_LINK_TYPE.ANYONE',
  4: 'AI_RISK_REPORT.SHAREPOINT.DEFAULT_SHARING_LINK_TYPE.EXISTING_ACCESS',
}

export const SHAREPOINT_DEFAULT_LINK_PERMISSION_MESSAGE_ID_BY_VALUE = {
  0: 'AI_RISK_REPORT.SHAREPOINT.DEFAULT_LINK_PERMISSION.NONE',
  1: 'AI_RISK_REPORT.SHAREPOINT.DEFAULT_LINK_PERMISSION.VIEW',
  2: 'AI_RISK_REPORT.SHAREPOINT.DEFAULT_LINK_PERMISSION.EDIT',
}

const hasMeasuredValue = (value) => value !== undefined && value !== null && value !== ''

const getTenantSettingValue = (tenantSettings, key) =>
  tenantSettings.find((entry) => hasMeasuredValue(entry?.[key]))?.[key] ?? null

const formatMessage = (intl, id, values) => intl.formatMessage({ id }, values)

const formatEnumValue = (intl, messageIdByValue, value) => {
  if (!hasMeasuredValue(value)) return null

  const messageId = messageIdByValue[String(value)]
  if (messageId) return `${value} (${formatMessage(intl, messageId)})`
  return String(value)
}

const normalizeBoolean = (value) => {
  if (value === true || value === false) return value

  const normalizedValue = String(value ?? '')
    .trim()
    .toLowerCase()
  if (normalizedValue === 'true' || normalizedValue === '1') return true
  if (normalizedValue === 'false' || normalizedValue === '0') return false
  return null
}

const formatBooleanValue = (value) => {
  const normalizedValue = normalizeBoolean(value)
  if (normalizedValue === true || normalizedValue === false) return String(normalizedValue)
  if (hasMeasuredValue(value)) return String(value)
  return null
}

const formatRecommendedBooleanValue = (value) => String(value)

const formatDaysValue = (intl, value) => {
  if (!hasMeasuredValue(value)) return null

  const dayCount = Number(value)
  if (!Number.isFinite(dayCount)) return String(value)

  return formatMessage(intl, 'AI_RISK_REPORT.SHAREPOINT.EXTERNAL_USER_EXPIRE_IN_DAYS.VALUE', {
    count: dayCount,
  })
}

const formatAnonymousLinksExpireInDaysValue = (intl, value) => {
  if (!hasMeasuredValue(value)) return null

  const dayCount = Number(value)
  if (dayCount === SHAREPOINT_NO_EXPIRY_DAYS_VALUE) {
    return formatMessage(intl, 'AI_RISK_REPORT.SHAREPOINT.REQUIRE_ANONYMOUS_LINKS_EXPIRE_IN_DAYS.NO_EXPIRY')
  }

  return formatDaysValue(intl, value)
}

const normalizeMarkNewFilesSensitiveByDefault = (value) => {
  if (value === SHAREPOINT_MARK_NEW_FILES_SENSITIVE_BLOCK_EXTERNAL_SHARING) return true
  if (value === SHAREPOINT_MARK_NEW_FILES_SENSITIVE_ALLOW_EXTERNAL_SHARING) return false
  return normalizeBoolean(value)
}

const formatMarkNewFilesSensitiveByDefaultValue = (value) => {
  const normalizedValue = normalizeMarkNewFilesSensitiveByDefault(value)
  if (normalizedValue === true || normalizedValue === false) return String(normalizedValue)
  if (hasMeasuredValue(value)) return String(value)
  return null
}

const getDefaultSharingLinkTypeTone = (value) => {
  if (!hasMeasuredValue(value)) return TONE.NEUTRAL

  const numericValue = Number(value)
  if (numericValue === 1) return TONE.OK
  if (numericValue === 3) return TONE.DANGER
  return TONE.WARN
}

const getDefaultLinkPermissionTone = (value) => {
  if (!hasMeasuredValue(value)) return TONE.NEUTRAL

  const numericValue = Number(value)
  if (numericValue === 1) return TONE.OK
  if (numericValue === 2) return TONE.DANGER
  return TONE.WARN
}

const getBooleanRecommendationTone = (value, recommendedValue) => {
  if (!hasMeasuredValue(value)) return TONE.NEUTRAL

  const normalizedValue = normalizeBoolean(value)
  if (normalizedValue === null) return TONE.WARN
  return normalizedValue === recommendedValue ? TONE.OK : TONE.DANGER
}

const getExternalUserExpireInDaysTone = (value, externalUserExpirationRequired) => {
  if (!hasMeasuredValue(value)) return TONE.NEUTRAL

  const normalizedExpirationRequired = normalizeBoolean(externalUserExpirationRequired)
  const dayCount = Number(value)
  if (normalizedExpirationRequired === false || dayCount === 0) return TONE.DANGER
  if (!Number.isFinite(dayCount)) return TONE.WARN
  if (
    normalizedExpirationRequired === true &&
    dayCount >= 1 &&
    dayCount <= SHAREPOINT_EXTERNAL_USER_EXPIRE_RECOMMENDED_DAYS
  ) {
    return TONE.OK
  }
  return TONE.WARN
}

const getRequireAnonymousLinksExpireInDaysTone = (value) => {
  if (!hasMeasuredValue(value)) return TONE.NEUTRAL

  const dayCount = Number(value)
  if (dayCount === SHAREPOINT_NO_EXPIRY_DAYS_VALUE || dayCount === 0) return TONE.DANGER
  if (!Number.isFinite(dayCount)) return TONE.WARN
  if (dayCount >= 1 && dayCount <= SHAREPOINT_ANONYMOUS_LINKS_EXPIRE_RECOMMENDED_DAYS) return TONE.OK
  return TONE.WARN
}

const getMarkNewFilesSensitiveByDefaultTone = (value) => {
  if (!hasMeasuredValue(value)) return TONE.NEUTRAL

  const normalizedValue = normalizeMarkNewFilesSensitiveByDefault(value)
  if (normalizedValue === null) return TONE.WARN
  return normalizedValue ? TONE.OK : TONE.DANGER
}

const requiresDlpPolicies = ({ value, hasDlpPolicies }) =>
  normalizeMarkNewFilesSensitiveByDefault(value) !== false && hasDlpPolicies === false

const requiresRestrictedAccessControlLicense = ({ value, hasLicense }) =>
  normalizeBoolean(value) !== true && hasLicense === false

const getRestrictedAccessControlTone = ({ value, hasLicense }) => {
  if (requiresRestrictedAccessControlLicense({ value, hasLicense })) return TONE.INFO

  if (!hasMeasuredValue(value)) return TONE.NEUTRAL

  const normalizedValue = normalizeBoolean(value)
  if (normalizedValue === null) return TONE.WARN
  return normalizedValue ? TONE.OK : TONE.DANGER
}

const BROAD_SHARING_VALUES = new Set([
  'anyone',
  'externalsharingonly',
  'externaluserandguestsharing',
  'externalusersharingonly',
  'externalusersandguestsharing',
])

const getSharingCapabilityTone = (value) => {
  if (!hasMeasuredValue(value)) return TONE.NEUTRAL
  return BROAD_SHARING_VALUES.has(String(value).toLowerCase()) ? TONE.DANGER : TONE.OK
}

export const buildSharePointTenantSettingRows = (tenantSettings, intl, { hasDlpPolicies = true } = {}) => {
  const sharingCapability = getTenantSettingValue(tenantSettings, 'sharingCapability')
  const isResharingByExternalUsersEnabled = getTenantSettingValue(tenantSettings, 'isResharingByExternalUsersEnabled')
  const isLegacyAuthProtocolsEnabled = getTenantSettingValue(tenantSettings, 'isLegacyAuthProtocolsEnabled')
  const defaultSharingLinkType = getTenantSettingValue(tenantSettings, 'defaultSharingLinkType')
  const defaultLinkPermission = getTenantSettingValue(tenantSettings, 'defaultLinkPermission')
  const externalUserExpirationRequired = getTenantSettingValue(tenantSettings, 'externalUserExpirationRequired')
  const externalUserExpireInDays = getTenantSettingValue(tenantSettings, 'externalUserExpireInDays')
  const requireAnonymousLinksExpireInDays = getTenantSettingValue(tenantSettings, 'requireAnonymousLinksExpireInDays')
  const markNewFilesSensitiveByDefault = getTenantSettingValue(tenantSettings, 'markNewFilesSensitiveByDefault')
  const enableRestrictedAccessControl = getTenantSettingValue(tenantSettings, 'enableRestrictedAccessControl')
  const hasRestrictedAccessControlLicense = getTenantSettingValue(tenantSettings, 'hasRestrictedAccessControlLicense')
  const isRestrictedAccessControlLicenseRequired = requiresRestrictedAccessControlLicense({
    value: enableRestrictedAccessControl,
    hasLicense: hasRestrictedAccessControlLicense,
  })

  return [
    {
      key: 'sharingCapability',
      setting: 'sharingCapability',
      current: sharingCapability,
      recommended: 'existingExternalUserSharingOnly',
      tone: getSharingCapabilityTone(sharingCapability),
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.SHAREPOINT.SHARING_CAPABILITY',
    },
    {
      key: 'defaultSharingLinkType',
      setting: 'defaultSharingLinkType',
      current: formatEnumValue(intl, SHAREPOINT_DEFAULT_SHARING_LINK_TYPE_MESSAGE_ID_BY_VALUE, defaultSharingLinkType),
      recommended: formatEnumValue(intl, SHAREPOINT_DEFAULT_SHARING_LINK_TYPE_MESSAGE_ID_BY_VALUE, 1),
      tone: getDefaultSharingLinkTypeTone(defaultSharingLinkType),
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.SHAREPOINT.DEFAULT_SHARING_LINK_TYPE',
    },
    {
      key: 'defaultLinkPermission',
      setting: 'defaultLinkPermission',
      current: formatEnumValue(intl, SHAREPOINT_DEFAULT_LINK_PERMISSION_MESSAGE_ID_BY_VALUE, defaultLinkPermission),
      recommended: formatEnumValue(intl, SHAREPOINT_DEFAULT_LINK_PERMISSION_MESSAGE_ID_BY_VALUE, 1),
      tone: getDefaultLinkPermissionTone(defaultLinkPermission),
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.SHAREPOINT.DEFAULT_LINK_PERMISSION',
    },
    {
      key: 'isResharingByExternalUsersEnabled',
      setting: 'isResharingByExternalUsersEnabled',
      current: formatBooleanValue(isResharingByExternalUsersEnabled),
      recommended: formatRecommendedBooleanValue(false),
      tone: getBooleanRecommendationTone(isResharingByExternalUsersEnabled, false),
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.SHAREPOINT.RESHARING_BY_EXTERNAL_USERS_ENABLED',
    },
    {
      key: 'externalUserExpirationRequired',
      setting: 'externalUserExpirationRequired',
      current: formatBooleanValue(externalUserExpirationRequired),
      recommended: formatRecommendedBooleanValue(true),
      tone: getBooleanRecommendationTone(externalUserExpirationRequired, true),
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.SHAREPOINT.EXTERNAL_USER_EXPIRATION_REQUIRED',
    },
    {
      key: 'externalUserExpireInDays',
      setting: 'externalUserExpireInDays',
      current: formatDaysValue(intl, externalUserExpireInDays),
      recommended: formatMessage(intl, 'AI_RISK_REPORT.SHAREPOINT.EXTERNAL_USER_EXPIRE_IN_DAYS.RECOMMENDED'),
      tone: getExternalUserExpireInDaysTone(externalUserExpireInDays, externalUserExpirationRequired),
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.SHAREPOINT.EXTERNAL_USER_EXPIRE_IN_DAYS',
    },
    {
      key: 'isLegacyAuthProtocolsEnabled',
      setting: 'isLegacyAuthProtocolsEnabled',
      current: formatBooleanValue(isLegacyAuthProtocolsEnabled),
      recommended: formatRecommendedBooleanValue(false),
      tone: getBooleanRecommendationTone(isLegacyAuthProtocolsEnabled, false),
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.SHAREPOINT.LEGACY_AUTH_PROTOCOLS_ENABLED',
    },
    {
      key: 'requireAnonymousLinksExpireInDays',
      setting: 'requireAnonymousLinksExpireInDays',
      current: formatAnonymousLinksExpireInDaysValue(intl, requireAnonymousLinksExpireInDays),
      recommended: formatMessage(intl, 'AI_RISK_REPORT.SHAREPOINT.REQUIRE_ANONYMOUS_LINKS_EXPIRE_IN_DAYS.RECOMMENDED'),
      tone: getRequireAnonymousLinksExpireInDaysTone(requireAnonymousLinksExpireInDays),
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.SHAREPOINT.REQUIRE_ANONYMOUS_LINKS_EXPIRE_IN_DAYS',
    },
    {
      key: 'markNewFilesSensitiveByDefault',
      setting: 'markNewFilesSensitiveByDefault',
      current: formatMarkNewFilesSensitiveByDefaultValue(markNewFilesSensitiveByDefault),
      recommended: formatRecommendedBooleanValue(true),
      tone: getMarkNewFilesSensitiveByDefaultTone(markNewFilesSensitiveByDefault),
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.SHAREPOINT.MARK_NEW_FILES_SENSITIVE_BY_DEFAULT',
      requiresDlpPolicies: requiresDlpPolicies({ value: markNewFilesSensitiveByDefault, hasDlpPolicies }),
    },
    {
      key: 'enableRestrictedAccessControl',
      setting: 'enableRestrictedAccessControl',
      current: isRestrictedAccessControlLicenseRequired
        ? formatMessage(intl, 'AI_RISK_REPORT.SHAREPOINT.RESTRICTED_ACCESS_CONTROL.LICENSE_REQUIRED')
        : formatBooleanValue(enableRestrictedAccessControl),
      recommended: formatRecommendedBooleanValue(true),
      tone: getRestrictedAccessControlTone({
        value: enableRestrictedAccessControl,
        hasLicense: hasRestrictedAccessControlLicense,
      }),
      tooltipId: 'AI_RISK_REPORT.TOOLTIP.SHAREPOINT.ENABLE_RESTRICTED_ACCESS_CONTROL',
      licenseRequired: isRestrictedAccessControlLicenseRequired,
    },
  ]
}
